import React from 'react';
import Chart from 'react-apexcharts';

const ConsultationDistributionMonthlyChart = ({ data = {} }) => {
  // Function to get last N days (oldest to newest)
  const getLastNDays = (days = 10) => {
    const dates = [];
    const today = new Date();
    
    // Start from (days-1) days ago and go to today (oldest to newest)
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      dates.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }
    
    return dates;
  };

  // Function to get last 10 days (for backward compatibility)
  const getLastTenDays = () => {
    return getLastNDays(10);
  };

  // Function to get last 10 days data (in chronological order)
  const getLastTenDaysData = () => {
    // Generate data that shows progression from older to newer dates
    const baseData = [20, 25, 30, 28, 35, 40, 38, 45, 42, 50];
    return baseData;
  };

  // Function to ensure data is in chronological order (oldest to newest)
  const ensureChronologicalOrder = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return dataArray;
    // If data comes from API in descending order, reverse it to ascending
    return [...dataArray].reverse();
  };
  const sameDayPctOrdered = ensureChronologicalOrder(data.sameDayConversionPct) || [];

  const chartData = {
    series: [
      {
        name: 'Total Attempt',
        data: ensureChronologicalOrder(data.totalAttempt) || getLastTenDaysData()
      },
      {
        name: 'Total Completed',
        data: ensureChronologicalOrder(data.totalCompleted) || getLastTenDaysData().map(val => Math.floor(val * 0.7))
      },
      {
        name: '1st Consultation',
        data: ensureChronologicalOrder(data.firstConsultation) || getLastTenDaysData().map(val => Math.floor(val * 0.4))
      },
      {
        name: 'Same Day Repeat Consultation',
        data: ensureChronologicalOrder(data.sameDayRepeatConsultation) || getLastTenDaysData().map(val => Math.floor(val * 0.2))
      }
    ],
    options: {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },
      colors: ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 6,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
          dataLabels: {
            position: 'top'
          }
        }
      },
      dataLabels: {
        enabled: false,
        formatter: function (val) {
          return val;
        },
        offsetY: -20,
        style: {
          fontSize: '12px',
          colors: ['#666'],
          fontWeight: '500'
        }
      },
      xaxis: {
        categories: ensureChronologicalOrder(data.dates) || getLastTenDays(),
        labels: {
          style: {
            colors: '#666',
            fontSize: '12px',
            fontWeight: '500'
          }
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        title: {
          text: 'Number of Consultations',
          style: {
            color: '#666',
            fontSize: '14px',
            fontWeight: '500'
          }
        },
        labels: {
          style: {
            colors: '#666',
            fontSize: '12px',
            fontWeight: '500'
          },
          formatter: function (val) {
            return Math.floor(val);
          }
        }
      },
      grid: {
        borderColor: '#f1f1f1',
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
        padding: {
          top: 20,
          right: 20,
          bottom: 20,
          left: 20
        }
      },
      states: {
        hover: {
          filter: {
            type: 'lighten',
            value: 0.1
          }
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'darken',
            value: 0.1
          }
        }
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        custom: function({ series, seriesIndex, dataPointIndex, w }) {
          const month = w.globals.labels[dataPointIndex];
          const totalValue = series.reduce((sum, s) => sum + (s[dataPointIndex] || 0), 0);
          const sameDayPctOfFirst = sameDayPctOrdered[dataPointIndex] != null ? sameDayPctOrdered[dataPointIndex] : 0;
          
          let tooltipContent = `
            <div style="padding: 16px; background: #fff; border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.15); border-left: 4px solid #3b82f6; min-width: 200px;">
              <div style="font-weight: 700; color: #242424; margin-bottom: 12px; font-size: 16px;">${month}</div>
              <div style="margin-bottom: 8px; font-weight: 600; color: #666; font-size: 14px;">Total: ${totalValue}</div>
          `;
          
          series.forEach((s, index) => {
            const value = s[dataPointIndex] || 0;
            const seriesName = w.globals.seriesNames[index];
            const color = w.globals.colors[index];
            const percentage = totalValue > 0 ? ((value / totalValue) * 100).toFixed(1) : 0;
            const isSameDayRepeat = seriesName === 'Same Day Repeat Consultation';
            const extraLine = isSameDayRepeat && sameDayPctOfFirst != null
              ? `<div style="color: #8b5cf6; font-size: 11px;">${sameDayPctOfFirst}% of 1st Consultation</div>`
              : '';
            
            tooltipContent += `
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; padding: 4px 0;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <div style="width: 12px; height: 12px; background: ${color}; border-radius: 3px;"></div>
                  <span style="color: #666; font-size: 13px;">${seriesName}:</span>
                </div>
                <div style="text-align: right;">
                  <div style="color: #242424; font-weight: 600; font-size: 13px;">${value}</div>
                  <div style="color: #999; font-size: 11px;">${percentage}%</div>
                  ${extraLine}
                </div>
              </div>
            `;
          });
          
          tooltipContent += `</div>`;
          return tooltipContent;
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        offsetY: -10,
        labels: {
          colors: '#666',
          useSeriesColors: false
        },
        markers: {
          width: 12,
          height: 12,
          radius: 6
        }
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 300
            },
            legend: {
              position: 'bottom',
              horizontalAlign: 'center'
            }
          }
        }
      ]
    }
  };

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h4>Consultation Distribution - Last 10 Days</h4>
        <p>Daily breakdown: Total Attempt, Total Completed, 1st Consultation, Repeat & Same Day Repeat (%; 1st consultation base) from {getLastTenDays()[0]} to {getLastTenDays()[getLastTenDays().length - 1]}</p>
      </div>
      <Chart
        options={chartData.options}
        series={chartData.series}
        type="bar"
        height={350}
      />
    </div>
  );
};

export default ConsultationDistributionMonthlyChart;
